/**
 * adminColors.js
 * Admin-portal color palette -- a literal-hex MIRROR of the Meridian token
 * layer in App.css :root (R6, Lane 220; DESIGN.md Decision 5 / §3.1-§3.3).
 *
 * Literal hex (not var(--token)) because several admin consumers set color via
 * bare SVG presentation attributes, where CSS var() does not resolve -- the same
 * rationale as the buyer `colors` mirror in SharedComponents.js (R2, Lane 77).
 *
 * The admin accent is PURPLE (--admin-accent) -- a deliberate "you are in the
 * internal admin area" orientation cue. Neutrals and semantics are UNIFIED with
 * the buyer/supplier token set (the admin portal differs by accent hue only);
 * the near-twin grays that used to drift here (#F5F7FA / #E1E5EB / #8A9BAE /
 * #1A2B3C) retire to the one token set.
 *
 * Shared components rendered inside the admin shell pick up the purple accent
 * automatically via the `.portal-admin` scope in App.css (which remaps
 * --color-brand* -> --admin-accent*); this file covers the hand-styled admin
 * chrome that references these constants directly.
 *
 * Retired 2026-07-19 (R6): `electricBlue` (#2251FF) -- the old buyer brand blue
 * used here as an accent. Admin chrome accents now use `accent` (purple).
 */
const adminColors = {
  // Admin accent (purple) -- mirrors --admin-accent* in App.css
  accent: '#7C3AED',                        // --admin-accent
  accentHover: '#6D28D9',                   // --admin-accent-hover
  accentLight: '#F3F0FF',                   // --admin-accent-light
  accentMuted: 'rgba(124, 58, 237, 0.08)',  // --admin-accent-muted
  adminPurple: '#7C3AED',                   // alias of `accent` (existing callsites)

  // Neutrals -- unified with the one token set (--color-*)
  white: '#FFFFFF',         // --color-surface
  surface: '#FFFFFF',       // --color-surface
  bg: '#F8F9FB',            // --color-bg
  lightGray: '#F8F9FB',     // --color-bg            (was #F5F7FA)
  border: '#E5E7EB',        // --color-border
  mediumGray: '#E5E7EB',    // --color-border        (was #E1E5EB)
  borderLight: '#F0F1F3',   // --color-border-light
  textPrimary: '#16181D',   // --color-text-primary
  darkText: '#16181D',      // --color-text-primary  (was #1A2B3C)
  textSecondary: '#6B7280', // --color-text-secondary
  textMuted: '#767B85',     // --color-text-muted    (was #8A9BAE)
  sidebarBg: '#16181D',     // --color-sidebar-bg
  deepBlue: '#16181D',      // alias -> unified dark sidebar (was #051C2C)

  // Semantic -- unified (--color-*)
  success: '#08875D',       // --color-success       (was #059669)
  warning: '#B8730B',       // --color-warning       (was #D97706)
  danger: '#D23B34',        // --color-danger        (was #DC2626)
  info: '#0EA5C4',          // --color-info (teal)
  cyan: '#0EA5C4',          // alias -> info teal    (was #00A9F4)
  supplierGreen: '#08875D', // alias -> success      (was #059669)
};

export default adminColors;
